import type { ParsedProfileResponse } from '@/api/gql/graphql';
import type {
  CertificationFormType,
  ExperienceFormType,
  FormationFormType,
  LanguagesFormType,
  ProfilFormType,
} from '@/types/form';
import { parseDate } from './date';
import dayjs from 'dayjs';

export const profileToFormValues = (profileResponse: ParsedProfileResponse | null) => {
  const experiencesValues: ExperienceFormType = {
    experiences: (profileResponse?.profile?.experiences || []).map(exp => ({
      title: exp.title || '',
      companyName: exp.companyName || '',
      description: exp.description || '',
      date_recommendation: exp.date_recommendation || '',
      description_quality_percentage: exp.description_quality_percentage || 0,
      description_recommendation: exp.description_recommendation || '',
      startDate: parseDate(exp.startDate),
      endDate: parseDate(exp.endDate),
      current: exp.endDate === 'Present',
      skills: exp.skills || [],
      location:
        typeof exp.location === 'string'
          ? exp.location
          : typeof exp.location === 'object'
            ? [exp.location?.city, exp.location?.province, exp.location?.country]
                .filter(Boolean)
                .join(', ')
            : '',
      workPlace: exp.workPlace || '',
    })),
  };

  const formationsValues: FormationFormType = {
    formations: (profileResponse?.profile?.educations || []).map(edu => ({
      title: edu.title || '',
      school: edu.school || '',
      description: edu.description || '',
      date_recommendation: edu.date_recommendation || '',
      description_quality_percentage: edu.description_quality_percentage || 0,
      description_recommendation: edu.description_recommendation || '',
      startDate: parseDate(edu.startDate),
      endDate: parseDate(edu.endDate),
      current: !edu.endDate || edu.endDate === 'Present',
    })),
  };

  const languagesValues: LanguagesFormType = {
    languages: (profileResponse?.profile?.languages || []).map(lang => ({
      language: lang.language || '',
      speaking_level: (lang.speaking_level || 'pro') as
        | 'beginner'
        | 'intermediate'
        | 'pro'
        | 'native',
      writing_level: (lang.writing_level || 'pro') as
        | 'beginner'
        | 'intermediate'
        | 'pro'
        | 'native',
    })),
  };

  const certificationsValues: CertificationFormType = {
    certifications: (profileResponse?.profile?.certifications || []).map(cert => ({
      title: cert.title || '',
      date: parseDate(cert.date) || dayjs(),
      url: cert.url || '',
      deliveredBy: cert.deliveredBy || '',
    })),
  };

  const personalValues: ProfilFormType = {
    // @ts-expect-error profileResponse?.profile?.name is not always available
    completeName: profileResponse?.profile?.name || profileResponse?.profile?.complete_name || '',
    headline: profileResponse?.profile?.headline || '',
    summary: profileResponse?.profile?.summary || '',
    summary_quality_percentage: profileResponse?.profile?.summary_quality_percentage || 0,
    summary_recommendation: profileResponse?.profile?.summary_recommendation || '',
    location:
      typeof profileResponse?.profile?.location === 'string'
        ? profileResponse?.profile?.location
        : typeof profileResponse?.profile?.location === 'object'
          ? [
              profileResponse?.profile?.location?.city,
              profileResponse?.profile?.location?.province,
              profileResponse?.profile?.location?.country,
            ]
              .filter(Boolean)
              .join(', ')
          : '',
    phone: profileResponse?.profile?.phone || '',
    email: profileResponse?.profile?.email || '',
    skills: profileResponse?.profile?.skills || [],
  };

  return {
    experiencesValues,
    formationsValues,
    languagesValues,
    certificationsValues,
    personalValues,
  };
};

export const formValuesToProfile = (
  experiencesValues: ExperienceFormType,
  formationsValues: FormationFormType,
  languagesValues: LanguagesFormType,
  certificationsValues: CertificationFormType,
  personalValues: ProfilFormType
): ParsedProfileResponse => {
  // Re-format location before updating profile
  const locationParts = personalValues.location?.split(',').map(part => part.trim()) || [];
  const formattedLocation = {
    city: locationParts[0] || null,
    province: locationParts[1] || null,
    country: locationParts[2] || null,
  };

  return {
    __typename: 'ParsedProfileResponse',
    content_is_cv: false,
    profile: {
      __typename: 'ParsedProfile',
      name: personalValues.completeName || '',
      headline: personalValues.headline || '',
      summary: personalValues.summary || '',
      location: formattedLocation,
      phone: personalValues.phone || '',
      email: personalValues.email || '',
      skills: personalValues.skills || [],
      experiences: experiencesValues.experiences.map(exp => ({
        __typename: 'ParsedExperience',
        title: exp.title,
        companyName: exp.companyName,
        description: exp.description || '',
        startDate: exp.startDate?.format('YYYY-MM-DD') || '',
        endDate: exp.current ? 'Present' : exp.endDate?.format('YYYY-MM-DD') || '',
        skills: exp.skills || [],
        location: {
          __typename: 'ParsedLocation',
          city: exp.location?.split(',')[0]?.trim() || null,
          province: exp.location?.split(',')[1]?.trim() || null,
          country: exp.location?.split(',')[2]?.trim() || null,
        },
        workPlace: exp.workPlace || '',
      })),
      educations: formationsValues.formations.map(edu => ({
        __typename: 'ParsedEducation',
        title: edu.title,
        school: edu.school,
        degree: edu.title, // Using title as degree since it's required
        description: edu.description || '',
        startDate: edu.startDate?.format('YYYY-MM-DD') || '',
        endDate: edu.current ? 'Present' : edu.endDate?.format('YYYY-MM-DD') || '',
      })),
      languages: languagesValues.languages.map(lang => ({
        __typename: 'ParsedLanguage',
        language: lang.language,
        speaking_level: lang.speaking_level,
        writing_level: lang.writing_level,
      })),
      certifications: certificationsValues.certifications.map(cert => ({
        __typename: 'ParsedCertification',
        title: cert.title,
        date: cert.date?.format('YYYY-MM-DD') || '',
        url: cert.url || '',
        deliveredBy: cert.deliveredBy || '',
      })),
    },
  };
};
